import React from 'react';
import { CustomizePracticeFormModalProps, CustomizePracticeFormModalValues } from './types';
import { SecondaryButton } from '@magoosh/lib/button';
import { Form, Modal, OverlayTrigger, Popover } from 'react-bootstrap';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import { Formik, FormikHelpers as FormikActions } from 'formik';
import * as yup from 'yup';
import RadioGroup from '@magoosh/lib/formik_inputs/field_radio_group';
import { paths } from 'config/path_helpers';
import style from './style.module.scss';
import cx from 'classnames';
import { some } from 'lodash';

const validationSchema = yup.object({
  practiceBlockName: yup.string().required('Please select a question type').label('Practice Type'),
  isQuizMode: yup.string().label('Mode Type').required()
});

export const CustomizePracticeFormModal: React.FC<CustomizePracticeFormModalProps> = (props) => {
  const { open, practiceBlocks, isQuizMode, toggleOpen } = props;

  const initialValues: CustomizePracticeFormModalValues = {
    practiceBlockName: practiceBlocks.length === 1 ? practiceBlocks[0].name : '',
    isQuizMode: isQuizMode ? 'true' : 'false'
  };

  const questionTypeAvailable = (name: string) => {
    return some(practiceBlocks, (block) => block.name === name);
  };

  const questionTypeOptions = [
    {
      label: 'Exercises',
      value: 'Exercises',
      hint: questionTypeAvailable('Exercises')
        ? "These are different from what you'll typically see on the GRE and are designed to check your understanding of the underlying concepts and skills you've learned."
        : 'None available.',
      disabled: !questionTypeAvailable('Exercises')
    },
    {
      label: 'Drills',
      value: 'Drills',
      hint: questionTypeAvailable('Drills')
        ? 'These are easier and simpler than questions on the actual test, but still in a test-like format.'
        : 'None available.',
      disabled: !questionTypeAvailable('Drills')
    },
    {
      label: 'Intermediate Test-Like Questions',
      value: 'Intermediate Test-Like Questions',
      hint: questionTypeAvailable('Intermediate Test-Like Questions')
        ? 'These are easy and medium difficulty questions similar to what you will see on the actual test.'
        : 'None available.',
      disabled: !questionTypeAvailable('Intermediate Test-Like Questions')
    },
    {
      label: 'Advanced Test-Like Questions',
      value: 'Advanced Test-Like Questions',
      hint: questionTypeAvailable('Advanced Test-Like Questions')
        ? 'These are hard and very hard difficulty questions similar to what you will see on the actual test.'
        : 'None available.',
      disabled: !questionTypeAvailable('Advanced Test-Like Questions')
    }
  ];

  const handleOnSubmit = (
    values: CustomizePracticeFormModalValues,
    { setSubmitting }: FormikActions<CustomizePracticeFormModalValues>
  ): void => {
    setSubmitting(true);

    const selectedPracticeBlock = practiceBlocks.find((b) => b.name === values.practiceBlockName);
    const params = {};
    if (typeof selectedPracticeBlock.difficultyMin === 'number') {
      params['difficulty_min'] = selectedPracticeBlock.difficultyMin;
    }
    if (typeof selectedPracticeBlock.difficultyMax === 'number') {
      params['difficulty_max'] = selectedPracticeBlock.difficultyMax;
    }
    params['quiz_mode'] = values.isQuizMode;

    window.location.assign(paths.contentBlockPractice(selectedPracticeBlock.id, params));
  };

  return (
    <Modal className={style.customizePracticeFormModal} show={open} onHide={() => toggleOpen(practiceBlocks)}>
      <Formik initialValues={initialValues} onSubmit={handleOnSubmit} validationSchema={validationSchema}>
        {({ values, isSubmitting, handleSubmit }) => (
          <Form>
            <Modal.Header className={cx(style.header, 'u-padding-B-n')} closeButton>
              <h3 className="u-margin-B-n">
                <strong>Customize Your Practice Session</strong>
              </h3>
            </Modal.Header>
            <Modal.Body className="u-padding-T-n">
              <hr />
              <h4>
                <strong>Choose Your Question Type</strong>{' '}
                <OverlayTrigger
                  trigger={['hover']}
                  placement="bottom"
                  rootClose={true}
                  overlay={
                    <Popover id="assessment-card-popover">
                      <p>Not all question types are available for every practice session.</p>
                      <p>
                        While not every segment has test-like questions, you'll see plenty in score
                        assessments, practice tests, and timed section practice.
                      </p>
                    </Popover>
                  }>
                  <small>
                    <FontAwesomeIcon icon={'info-circle'} />
                  </small>
                </OverlayTrigger>
              </h4>

              <RadioGroup
                className={cx(style.radioOption, 'u-margin-B-n')}
                name="practiceBlockName"
                optionsArray={questionTypeOptions}
                value={values.practiceBlockName}
              />

              <hr className="u-margin-T-n" />
              <h4>
                <strong>Choose Your Mode</strong>{' '}
                <OverlayTrigger
                  trigger={['hover']}
                  placement="bottom"
                  rootClose={true}
                  overlay={
                    <Popover id="assessment-card-popover">
                      <p>In practice mode, you will see explanations after answering each question.</p>
                      <p>In quiz mode, all explanations are hidden until the end of the practice session.</p>
                    </Popover>
                  }>
                  <small>
                    <FontAwesomeIcon icon={'info-circle'} />
                  </small>
                </OverlayTrigger>
              </h4>
              <RadioGroup
                className={style.radioOption}
                name="isQuizMode"
                optionsArray={[
                  {
                    label: 'Practice',
                    value: 'false'
                  },
                  {
                    label: 'Quiz',
                    value: 'true'
                  }
                ]}
                value={values.isQuizMode}
              />
              <SecondaryButton
                disabled={isSubmitting}
                onClick={handleSubmit}
                block={true}
                className="u-margin-T-m">
                Start Session
              </SecondaryButton>
            </Modal.Body>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default CustomizePracticeFormModal;
